import React from 'react';
import { CustomerByODService, FluxClientODDown } from 'types';
import styled from 'styled-components';
import { Tooltip } from 'antd';
import { TooltipPlacement } from 'antd/lib/tooltip';

import { BASIC_ROW_HEIGHT, Basic, getTooltipTitle, isValueStringEmpty } from './utils';

type LeftTooltipKey = 'arrivalStationFullName';
type RightTooltipKey =
  | 'allDepartureNbPassengersDown'
  | 'firstTrainAssistanceDown'
  | 'secondTrainAssistanceDown'
  | 'firstTrainUfrDown'
  | 'secondTrainUfrDown'
  | 'firstTrainChildDown'
  | 'secondTrainChildDown';

interface Props {
  customerService: CustomerByODService;
  fluxClientODKey: keyof FluxClientODDown;
  formatDisplay?: (value: Basic) => string;
  tooltipPlacement?: TooltipPlacement;
  tooltipTitle?: string;
  leftTooltipKey?: LeftTooltipKey;
  rightTooltipKeys?: Array<RightTooltipKey>;
}

type StyledFluxClientDisplayItemProps = {
  empty: boolean;
  hasException?: boolean;
};

const StyledFluxClientDisplayItem = styled.div<StyledFluxClientDisplayItemProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  ${(props) => props.hasException && 'color: red'};
  height: ${BASIC_ROW_HEIGHT}px;
  padding-top: 5px;
  padding-bottom: 5px;
  &:not(:last-child) {
    border-bottom: 2px solid #d8d8d8;
  }
  &:after {
    content: ${(props) => (props.empty ? '"\xA0"' : '""')};
  }
`;

const FluxClientDownDisplay: React.FC<Props> = ({
  tooltipPlacement = 'left',
  tooltipTitle = '',
  customerService,
  fluxClientODKey,
  formatDisplay,
  leftTooltipKey,
  rightTooltipKeys,
}) => {
  const HasTooltipTitleProp = ({ value, title }: { value: Basic; title: string }) => (
    <Tooltip title={title} placement={tooltipPlacement}>
      {formatDisplay ? formatDisplay(value) : value}
    </Tooltip>
  );

  const HasOnlyLeftTooltipKey = ({ value, title }: { value: Basic; title: string }) => (
    <Tooltip title={title} placement="left">
      {formatDisplay ? formatDisplay(value) : value}
    </Tooltip>
  );

  const HasOnlyRightTooltipKeys = ({ value, title }: { value: Basic; title: string }) => (
    <Tooltip title={title} placement="right">
      {formatDisplay ? formatDisplay(value) : value}
    </Tooltip>
  );

  const HasOnlyBothLeftAndRightTooltipKeys = ({
    value,
    titleLeft,
    titleRight,
  }: {
    value: Basic;
    titleLeft: string;
    titleRight: string;
  }) => (
    <Tooltip title={titleRight} placement="right">
      <Tooltip title={titleLeft} placement="left">
        {formatDisplay ? formatDisplay(value) : value}
      </Tooltip>
    </Tooltip>
  );

  return (
    <div>
      {customerService?.fluxClientODs &&
        customerService?.fluxClientODs.map((fluxClientItem) =>
          fluxClientItem.fluxClientODDown.map((downItem) => (
            <StyledFluxClientDisplayItem
              key={`${fluxClientODKey}_${downItem.arrivalStationUic}`}
              empty={isValueStringEmpty(downItem[fluxClientODKey])}
              hasException={customerService.isCancel || downItem.isArrivalStationStopSkip}
            >
              {!!tooltipTitle && <HasTooltipTitleProp value={downItem[fluxClientODKey]} title={tooltipTitle} />}

              {!tooltipTitle && !rightTooltipKeys && !!leftTooltipKey && (
                <HasOnlyLeftTooltipKey
                  value={downItem[fluxClientODKey]}
                  title={getTooltipTitle({ value1: downItem[leftTooltipKey] })}
                />
              )}

              {!tooltipTitle && !leftTooltipKey && !!rightTooltipKeys && (
                <HasOnlyRightTooltipKeys
                  value={downItem[fluxClientODKey]}
                  title={
                    rightTooltipKeys.length === 1
                      ? getTooltipTitle({ value1: downItem[rightTooltipKeys[0]] })
                      : getTooltipTitle({
                          value1: downItem[rightTooltipKeys[0]],
                          value2: downItem[rightTooltipKeys[1]],
                        })
                  }
                />
              )}

              {!tooltipTitle && !!rightTooltipKeys && !!leftTooltipKey && (
                <HasOnlyBothLeftAndRightTooltipKeys
                  value={downItem[fluxClientODKey]}
                  titleLeft={getTooltipTitle({ value1: downItem[leftTooltipKey] })}
                  titleRight={
                    rightTooltipKeys.length === 1
                      ? getTooltipTitle({ value1: downItem[rightTooltipKeys[0]] })
                      : getTooltipTitle({
                          value1: downItem[rightTooltipKeys[0]],
                          value2: downItem[rightTooltipKeys[1]],
                        })
                  }
                />
              )}

              {!tooltipTitle && !rightTooltipKeys && !leftTooltipKey && (
                <>{formatDisplay ? formatDisplay(downItem[fluxClientODKey]) : downItem[fluxClientODKey]}</>
              )}
            </StyledFluxClientDisplayItem>
          ))
        )}
    </div>
  );
};

export default FluxClientDownDisplay;
